import { type FC, type PropsWithChildren, useEffect } from "react";
import { FullPageLoader } from "../components/FullPageLoader";
import { AppShell } from "./components/AppShell";
import { FullPageError } from "../components/FullPageError";
import { useTRPC } from "../trpc";
import { useBasePath } from "../hooks/router";
import { useIntl } from "react-intl";
import { useSetUser, useUser } from "../hooks/user";
import { useSetLocale } from "../hooks/locale";
import { useSetDefaultApiKey } from "../hooks/apiKey";
import { useQuery } from "@tanstack/react-query";
import { appShellMain } from "./Dashboard.module.css";
import { useAboveFreeModal } from "../hooks/payment";
import { useNavigateUserLayout } from "../routes/auth/hooks/navigate";
import type { Locale } from "@project/common";
import type { NavMenuOptions } from "../types/menu";

interface DashboardLayoutProps extends PropsWithChildren {
  menu: NavMenuOptions;
}

const shellClassNames = {
  main: appShellMain,
};

export const DashboardLayout: FC<DashboardLayoutProps> = ({ children, menu }) => {
  const user = useUser();
  const setUser = useSetUser();
  const navigate = useNavigateUserLayout();
  const setLocale = useSetLocale();
  const setApiKey = useSetDefaultApiKey();
  const intl = useIntl();
  const pathname = useBasePath();
  const trpc = useTRPC();
  const modal = useAboveFreeModal();

  const { data, isLoading, isError, error } = useQuery(trpc.getCurrentUser.queryOptions(undefined, {
    enabled: !user,
  }));

  const { data: apiKey } = useQuery(trpc.getDefaultApiKey.queryOptions(undefined, {
    enabled: Boolean(user),
    refetchInterval: 1000 * 60 * 60,
    refetchOnMount: false,
  }));

  useEffect(() => {
    if (data) {
      if (data.locale !== intl.locale) {
        setLocale(data.locale as Locale);
      }
    }
  }, [data, intl, setLocale]);

  useEffect(() => {
    if (apiKey) {
      setApiKey(apiKey);
    }
  }, [apiKey]);

  useEffect(() => {
    if (!user && data) {
      setUser(data);
      navigate(data);
    }
  }, [user, data, setUser, navigate, pathname]);

  if (isLoading) {
    return <FullPageLoader />;
  }
  if (isError) {
    return <FullPageError error={error} />;
  }
  if (!user || !apiKey) {
    return <FullPageLoader />;
  }

  return (
    <AppShell menu={menu} classNames={shellClassNames}>
      {children}
      {modal}
    </AppShell>
  );
};
